import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { handleTokenRefresh } from "./refreshTokenHelper";

/**
 * Sets up the response interceptor for automatic token refresh on 401 errors
 * @param axiosInstance - The axios instance to add the interceptor to
 */
export const setupRefreshTokenInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // Check for 401 errors - either from HTTP status or response data
      const responseData = error.response?.data as any;
      const is401Error = 
        error.response?.status === 401 || 
        (responseData && responseData.status === 401);

      // Handle 401 Unauthorized errors
      if (is401Error && originalRequest) {
        // Prevent infinite loop - don't retry if this is already a retry or if it's the refresh token endpoint
        const isRefreshTokenEndpoint = 
          originalRequest.url?.includes("/refresh-token") ||
          originalRequest.url?.includes("/users/refresh-token");
        
        if (originalRequest._retry || isRefreshTokenEndpoint) {
          // Refresh token failed or already retried, clear auth and reject
          console.warn("Refresh token endpoint returned 401 or request already retried. Clearing auth.", {
            url: originalRequest.url,
            isRetry: originalRequest._retry,
            isRefreshEndpoint: isRefreshTokenEndpoint,
          });
          localStorage.removeItem("authUser");
          localStorage.removeItem("userPermissions");
          return Promise.reject(error);
        }

        try {
          // Mark request as retried
          originalRequest._retry = true;

          // Attempt to refresh token
          const newAccessToken = await handleTokenRefresh();

          // Update the authorization header with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          }

          // Retry the original request with new token
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear auth data
          localStorage.removeItem("authUser");
          localStorage.removeItem("userPermissions");

          // Optionally redirect to login page
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }

          return Promise.reject(refreshError);
        }
      }

      // Handle other errors
      if (error.response) {
        const responseData = error.response.data as any;
        const status = error.response.status || responseData?.status;
        console.error(
          ` API error [${status}]:`,
          error.response.data
        );
      } else {
        console.error("API error (no response):", error);
      }

      return Promise.reject(error);
    }
  );
};

